import * as React from "react";
import { useState, useEffect, useCallback, useMemo } from "react";
import isEqual from "lodash/isEqual";
import URI from "urijs";
import useLocation from "routing/useLocation";
import useQuery from "routing/useQuery";
import useActiveQueryId from "views/hooks/useActiveQueryId";
import useWidgets from "views/hooks/useWidgets";
import useViewsDispatch from "views/stores/useViewsDispatch";
import useHistory from "routing/useHistory";
import { executeActiveQuery, setWidgetToSearch } from "views/logic/slices/viewSlice";
import { selectSearchTypesToSearch } from "views/logic/slices/searchExecutionSelectors";
import useAppSelector from "stores/useAppSelector";
import useView from "views/hooks/useView";
import { setSearchTypesToSearch } from "views/logic/slices/searchExecutionSlice";
import { setNewWidget } from "views/logic/slices/widgetsSlice";
import WidgetFocusContext from "./WidgetFocusContext";
const _clearURI = (query) => new URI(query).removeSearch("focusing").removeSearch("editing").removeSearch("focusedId");
const _updateQueryParams = (newQueryParams, query) => {
  let baseUri = _clearURI(query);
  if (newQueryParams) {
    if (newQueryParams.id && (newQueryParams.focusing || newQueryParams.editing)) {
      baseUri = baseUri.setSearch("focusedId", newQueryParams.id);
    }
    if (newQueryParams.focusing) {
      baseUri = baseUri.setSearch("focusing", String(true));
    }
    if (newQueryParams.editing) {
      baseUri = baseUri.setSearch("editing", String(true));
    }
  }
  return baseUri.toString();
};
const emptyFocusContext = {
  editing: false,
  focusing: false,
  id: void 0
};
const useSyncStateWithQueryParams = ({ focusedWidget, focusUriParams, setFocusedWidget, widgetIds }) => {
  const dispatch = useViewsDispatch();
  const { widgetMapping } = useView();
  const searchTypesToSearch = useAppSelector(selectSearchTypesToSearch);
  useEffect(() => {
    const nextFocusedWidget = focusUriParams.focusing || focusUriParams.editing ? {
      id: focusUriParams.id,
      editing: focusUriParams.editing,
      focusing: true
    } : emptyFocusContext;
    if (!isEqual(focusedWidget != null ? focusedWidget : emptyFocusContext, nextFocusedWidget)) {
      if (focusUriParams.id && !widgetIds.includes(focusUriParams.id)) {
        return;
      }
      setFocusedWidget(nextFocusedWidget);
      dispatch(setWidgetToSearch(nextFocusedWidget.id));
      dispatch(executeActiveQuery());
    }
  }, [focusedWidget, setFocusedWidget, widgetIds, focusUriParams, dispatch]);
  useEffect(() => {
    var _a, _b;
    if (focusedWidget) {
      const searchTypeIds = (_b = (_a = widgetMapping.get(focusedWidget.id)) == null ? void 0 : _a.toArray()) != null ? _b : [];
      const searchTypesToSearchIsUpToDate = searchTypeIds.length === 0 || !searchTypesToSearch || searchTypesToSearch.length === 0 || searchTypeIds.every((id) => searchTypesToSearch == null ? void 0 : searchTypesToSearch.includes(id));
      if (!searchTypesToSearchIsUpToDate) {
        dispatch(setSearchTypesToSearch(searchTypeIds));
      }
    }
  }, [dispatch, focusedWidget, searchTypesToSearch, widgetMapping]);
};
const useCleanupQueryParams = ({ focusUriParams, widgetIds, query, history }) => {
  useEffect(() => {
    if ((focusUriParams == null ? void 0 : focusUriParams.id) && !widgetIds.includes(focusUriParams.id) && focusUriParams.isPageShown || (focusUriParams == null ? void 0 : focusUriParams.id) === void 0) {
      const baseURI = _clearURI(query);
      const newQuery = baseURI.toString();
      if (query !== newQuery) {
        history.replace(newQuery);
      }
    }
  }, [focusUriParams, widgetIds, query, history]);
};
const WidgetFocusProvider = ({ children }) => {
  const { search, pathname } = useLocation();
  const query = pathname + search;
  const history = useHistory();
  const [focusedWidget, setFocusedWidget] = useState();
  const widgets = useWidgets();
  const widgetIds = useMemo(() => widgets.map((widget) => widget.id).toArray(), [widgets]);
  const activeQuery = useActiveQueryId();
  const params = useQuery();
  const focusUriParams = useMemo(
    () => ({
      editing: params.editing === "true",
      focusing: params.focusing === "true",
      id: params.focusedId,
      isPageShown: !params.page || params.page === activeQuery
    }),
    [params.editing, params.focusing, params.focusedId, params.page, activeQuery]
  );
  const dispatch = useViewsDispatch();
  useSyncStateWithQueryParams({ focusedWidget, setFocusedWidget, widgetIds, focusUriParams });
  useCleanupQueryParams({ focusUriParams, widgetIds, query, history });
  const updateFocusQueryParams = useCallback(
    (newQueryParams) => {
      const newURI = _updateQueryParams(newQueryParams, query);
      if (newURI !== query) {
        history.replace(newURI);
      }
    },
    [history, query]
  );
  const setWidgetFocusing = useCallback(
    (widgetId) => updateFocusQueryParams({
      id: widgetId,
      editing: false,
      focusing: true
    }),
    [updateFocusQueryParams]
  );
  const unsetWidgetFocusing = useCallback(() => {
    if (focusUriParams.id) {
      dispatch(setNewWidget(focusUriParams.id));
    }
    updateFocusQueryParams(void 0);
  }, [dispatch, focusUriParams.id, updateFocusQueryParams]);
  const setWidgetEditing = useCallback(
    (widgetId) => {
      updateFocusQueryParams({
        id: widgetId,
        editing: true,
        focusing: focusUriParams.focusing
      });
    },
    [focusUriParams.focusing, updateFocusQueryParams]
  );
  const unsetWidgetEditing = useCallback(() => {
    if (!focusUriParams.focusing && focusUriParams.id) {
      dispatch(setNewWidget(focusUriParams.id));
    }
    updateFocusQueryParams({
      id: focusUriParams.focusing && focusUriParams.id ? focusUriParams.id : void 0,
      editing: false,
      focusing: focusUriParams.focusing
    });
  }, [dispatch, focusUriParams.focusing, focusUriParams.id, updateFocusQueryParams]);
  const widgetFocusContextValue = useMemo(
    () => ({
      focusedWidget,
      setWidgetFocusing,
      setWidgetEditing,
      unsetWidgetEditing,
      unsetWidgetFocusing
    }),
    [focusedWidget, setWidgetFocusing, setWidgetEditing, unsetWidgetEditing, unsetWidgetFocusing]
  );
  return /* @__PURE__ */ React.createElement(WidgetFocusContext.Provider, { value: widgetFocusContextValue }, children);
};
export default WidgetFocusProvider;
